/* RegistroUsoPublic.jsx — Formulario público de bitácora de uso de unidades.
   Ruta: /registro-uso  (sin login)
   Octavio 18-jun-2026.
*/
import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AREAS = [
  "ADMINISTRATIVO",
  "ALUMBRADO PUBLICO",
  "ALMACEN",
  "AREAS VERDES CONTRATADAS",
  "CONTROL VEHICULAR",
  "FONTANERIA",
  "FORESTACION / VIVERO",
  "HERRERIA",
  "MAQUINARIA",
  "MONUMENTOS",
  "OBRA MENOR",
  "PARQUES Y JARDINES / VIALIDADES",
  "PIPAS",
  "PODA Y DERRIBOS",
  "TRABAJOS ESPECIALES",
];

function hoyISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function RegistroUsoPublic() {
  const [noEco, setNoEco] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [unidadOk, setUnidadOk] = useState(false);
  const [unidadError, setUnidadError] = useState("");    // bloquea (409 excluida)
  const [unidadAviso, setUnidadAviso] = useState("");    // informativo no bloquea (404)
  const [chofer, setChofer] = useState("");
  const [noEmp, setNoEmp] = useState("");
  const [area, setArea] = useState("");
  const [odomFunciona, setOdomFunciona] = useState(null); // null | true | false
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const debRef = useRef(null);

  const buscarUnidad = async (val) => {
    const v = (val || "").trim();
    setUnidadOk(false);
    setDescripcion("");
    setUnidadError("");
    setUnidadAviso("");
    if (!v) return;
    try {
      const r = await axios.get(`${API}/uso-diario/buscar-unidad/${encodeURIComponent(v)}`);
      setDescripcion(r.data.descripcion || "");
      setUnidadOk(true);
    } catch (e) {
      setUnidadOk(false);
      if (e?.response?.status === 409) {
        // Unidad excluida (REMOLQUE/TRACTOR/etc) → BLOQUEA
        setUnidadError(e.response.data?.detail || "Unidad no aplica");
      } else if (e?.response?.status === 404) {
        // Octavio 19-jun-2026: NO bloquea, solo informa.
        setUnidadAviso("Unidad no encontrada en catálogo — se registrará de todas formas");
      }
    }
  };

  const onChangeEco = (val) => {
    setNoEco(val);
    if (debRef.current) clearTimeout(debRef.current);
    const v = val.trim();
    if (v.length >= 3) {
      debRef.current = setTimeout(() => buscarUnidad(v), 350);
    } else {
      setUnidadOk(false);
      setDescripcion("");
      setUnidadError("");
      setUnidadAviso("");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!noEco.trim()) return toast.error("Escribe el No. de unidad");
    if (unidadError) return toast.error(unidadError);  // solo bloquea si es excluida (409)
    if (chofer.trim().length < 5) return toast.error("Nombre del chofer mínimo 5 caracteres");
    if (!noEmp.trim()) return toast.error("Escribe tu No. de empleado");
    if (!area) return toast.error("Selecciona tu área");
    if (odomFunciona === null) return toast.error("Indica si el odómetro funciona");

    setBusy(true);
    try {
      const body = {
        no_economico: noEco.trim(),
        descripcion_unidad: descripcion,
        nombre_chofer: chofer.trim(),
        no_empleado: noEmp.trim(),
        area,
        odometro_funciona: odomFunciona,
        fecha: hoyISO(),
      };
      const r = await axios.post(`${API}/uso-diario/registrar`, body);
      setDone(r.data.registro);
      toast.success("Registro guardado correctamente");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Error al registrar";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setNoEco(""); setDescripcion(""); setUnidadOk(false);
    setUnidadError(""); setUnidadAviso("");
    setChofer(""); setNoEmp(""); setArea(""); setOdomFunciona(null);
    setDone(null);
  };

  if (done) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={{ textAlign: "center" }}>
            <div style={{
              width: 64, height: 64, borderRadius: 999, background: "#DCFCE7",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              marginBottom: 16,
            }}>
              <i className="fa-solid fa-circle-check" style={{ fontSize: 36, color: "#15803D" }}/>
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: 0 }}>
              ¡Registro guardado!
            </h2>
            <p style={{ color: "#64748B", fontSize: 14, marginTop: 6 }}>
              Gracias por tu registro.
            </p>
          </div>
          <div style={{ background: "#F8FAFC", borderRadius: 8, padding: 14, marginTop: 18, fontSize: 13 }}
               data-testid="uso-done-resumen">
            <div><strong>Fecha:</strong> {done.fecha}</div>
            <div><strong>Unidad:</strong> {done.no_economico} · {done.descripcion_unidad}</div>
            <div><strong>Chofer:</strong> {done.nombre_chofer}</div>
            <div><strong>No. empleado:</strong> {done.no_empleado}</div>
            <div><strong>Área:</strong> {done.area}</div>
            <div><strong>Odómetro funciona:</strong> {done.odometro_funciona ? "Sí" : "No"}</div>
          </div>
          <button
            type="button"
            onClick={reset}
            data-testid="btn-otro-registro"
            style={{ ...styles.btnPrimary, marginTop: 20 }}
          >
            <i className="fa-solid fa-plus mr-2"/> Registrar otra unidad
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page} data-testid="page-registro-uso">
      <div style={styles.card}>
        {/* Encabezado */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={styles.logo}>
            <span style={{ color: "#C9A84C", fontWeight: 900, fontSize: 14, lineHeight: 1 }}>DMU</span>
            <span style={{ color: "white", fontWeight: 800, fontSize: 11 }}>614</span>
          </div>
          <div>
            <div style={{ fontWeight: 800, color: "#0F172A", fontSize: 17 }}>
              Registro de uso de unidad
            </div>
            <div style={{ fontSize: 12, color: "#64748B" }}>DMU 614 · Mantenimiento Urbano</div>
          </div>
        </div>

        {/* Badge cierre — desactivado; descomentar y ajustar fecha/hora para reactivar
        <div data-testid="badge-cierre" style={{
          marginTop: 14, padding: "8px 12px", borderRadius: 8, background: "#FEE2E2",
          color: "#991B1B", fontWeight: 700, fontSize: 13, textAlign: "center",
          border: "1px solid #FCA5A5",
        }}>
          <i className="fa-solid fa-clock mr-2"/>Cierra sábado 20 de junio a la 1:00 PM
        </div>
        */}

        <form onSubmit={onSubmit} style={{ marginTop: 16 }} data-testid="form-registro-uso">
          {/* No. unidad */}
          <Campo label="No. unidad *">
            <input
              type="text"
              value={noEco}
              onChange={(e) => onChangeEco(e.target.value)}
              data-testid="uso-inp-no-eco"
              placeholder="Ej. 12798"
              style={styles.inp}
              autoComplete="off"
            />
            {unidadOk && (
              <div data-testid="uso-unidad-ok" style={{ marginTop: 6, padding: "6px 10px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 6, fontSize: 12, color: "#166534" }}>
                <i className="fa-solid fa-circle-check mr-1"/>{descripcion}
              </div>
            )}
            {unidadError && (
              <div data-testid="uso-unidad-error" style={{ marginTop: 6, padding: "6px 10px", background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 6, fontSize: 12, color: "#991B1B" }}>
                <i className="fa-solid fa-triangle-exclamation mr-1"/>{unidadError}
              </div>
            )}
            {unidadAviso && (
              <div data-testid="uso-unidad-aviso" style={{ marginTop: 6, padding: "6px 10px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 6, fontSize: 12, color: "#1E3A8A" }}>
                <i className="fa-solid fa-circle-info mr-1"/>{unidadAviso}
              </div>
            )}
          </Campo>

          {/* Pregunta: ¿El odómetro funciona? — reemplaza el campo kilometraje (Octavio 19-jun-2026).
              Visible si la unidad fue encontrada O si fue aceptada como "no catalogada". */}
          {(unidadOk || unidadAviso) && (
            <Campo label="¿El odómetro funciona? *">
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setOdomFunciona(true)}
                  data-testid="uso-btn-odom-si"
                  style={{
                    flex: 1, padding: "12px 16px", border: "2px solid",
                    borderColor: odomFunciona === true ? "#15803D" : "#CBD5E1",
                    background: odomFunciona === true ? "#DCFCE7" : "white",
                    color: odomFunciona === true ? "#14532D" : "#475569",
                    fontWeight: 700, fontSize: 15, borderRadius: 10, cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  <i className="fa-solid fa-circle-check mr-2" style={{ color: odomFunciona === true ? "#15803D" : "#94A3B8" }}/>
                  SÍ
                </button>
                <button
                  type="button"
                  onClick={() => setOdomFunciona(false)}
                  data-testid="uso-btn-odom-no"
                  style={{
                    flex: 1, padding: "12px 16px", border: "2px solid",
                    borderColor: odomFunciona === false ? "#B91C1C" : "#CBD5E1",
                    background: odomFunciona === false ? "#FEE2E2" : "white",
                    color: odomFunciona === false ? "#7F1D1D" : "#475569",
                    fontWeight: 700, fontSize: 15, borderRadius: 10, cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  <i className="fa-solid fa-circle-xmark mr-2" style={{ color: odomFunciona === false ? "#B91C1C" : "#94A3B8" }}/>
                  NO
                </button>
              </div>
            </Campo>
          )}

          {/* Chofer */}
          <Campo label="Nombre del chofer *">
            <input
              type="text"
              value={chofer}
              onChange={(e) => setChofer(e.target.value.toUpperCase())}
              data-testid="uso-inp-chofer"
              placeholder="JUAN PEREZ GOMEZ"
              minLength={5}
              style={{ ...styles.inp, textTransform: "uppercase" }}
              autoComplete="off"
            />
          </Campo>

          {/* No. empleado */}
          <Campo label="No. empleado *">
            <input
              type="text"
              value={noEmp}
              onChange={(e) => setNoEmp(e.target.value)}
              data-testid="uso-inp-no-emp"
              placeholder="Ej. 20836"
              style={styles.inp}
              autoComplete="off"
            />
          </Campo>

          {/* Área */}
          <Campo label="Área *">
            <select
              value={area}
              onChange={(e) => setArea(e.target.value)}
              data-testid="uso-sel-area"
              style={styles.inp}
            >
              <option value="">— Selecciona tu área —</option>
              {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Campo>

          <button
            type="submit"
            disabled={busy}
            data-testid="uso-btn-submit"
            style={{ ...styles.btnPrimary, marginTop: 12, opacity: busy ? 0.6 : 1 }}
          >
            <i className="fa-solid fa-paper-plane mr-2"/>
            {busy ? "Guardando…" : "Registrar uso"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Campo({ label, children }) {
  return (
    <label style={{ display: "block", marginTop: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
        {label}
      </div>
      {children}
    </label>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #0F172A 0%, #1E293B 100%)",
    padding: "32px 16px",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  },
  card: {
    width: "100%",
    maxWidth: 480,
    background: "white",
    borderRadius: 16,
    padding: 22,
    boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
  },
  logo: {
    width: 48, height: 48, borderRadius: 12, background: "#0F172A",
    display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
    border: "2px solid #C9A84C",
  },
  inp: {
    width: "100%", border: "1px solid #CBD5E1", borderRadius: 8,
    padding: "10px 12px", fontSize: 14, color: "#0F172A", background: "white",
    boxSizing: "border-box",
  },
  btnPrimary: {
    width: "100%", background: "#0F172A", color: "white", border: 0,
    padding: "12px 16px", borderRadius: 10, fontSize: 15, fontWeight: 800,
    cursor: "pointer",
  },
};
